import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { id: "profile", label: "About you" },
  { id: "business", label: "Your practice" },
  { id: "page", label: "Patient page" },
  { id: "plan", label: "Choose plan" },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, refreshUser, logout } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Form states
  const [profile, setProfile] = useState({
    owner_first_name: "",
    owner_last_name: "",
    owner_title: "Owner / Lead Orthodontist",
    owner_phone: "",
    country: "",
  });
  const [biz, setBiz] = useState({
    locations_count: "1",
    monthly_case_volume: "10-25",
    referral_source: "",
  });
  const [page, setPage] = useState({
    clinic_tagline: "Transform your smile with clear aligners.",
    treatment_name: "clear aligners",
    primary_color: "#1C352D",
    booking_url: "",
    phone: "",
    address: "",
    logo_url: "",
  });

  useEffect(() => {
    if (user) {
      setProfile({
        owner_first_name: user.owner_first_name || "",
        owner_last_name: user.owner_last_name || "",
        owner_title: user.owner_title || "Owner / Lead Orthodontist",
        owner_phone: user.owner_phone || "",
        country: user.country || "",
      });
      setBiz({
        locations_count: user.locations_count || "1",
        monthly_case_volume: user.monthly_case_volume || "10-25",
        referral_source: user.referral_source || "",
      });
      setPage({
        clinic_tagline: user.clinic_tagline || "Transform your smile with clear aligners.",
        treatment_name: user.treatment_name || "clear aligners",
        primary_color: user.primary_color || "#1C352D",
        booking_url: user.booking_url || "",
        phone: user.phone || "",
        address: user.address || "",
        logo_url: user.logo_url || "",
      });
    }
  }, [user]);

  const next = async () => {
    setSaving(true);
    try {
      if (step === 0) {
        // Profile is now fully optional — anything entered is saved
        await api.post("/onboarding/profile", profile);
      } else if (step === 1) {
        await api.post("/onboarding/business", biz);
      } else if (step === 2) {
        await api.post("/onboarding/clinic-page", page);
        await api.post("/onboarding/complete");
        await refreshUser();
      }
      setStep((s) => s + 1);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  // Fast-path: save whatever is filled and jump straight to the dashboard.
  const skipAll = async () => {
    setSaving(true);
    try {
      if (step === 0) await api.post("/onboarding/profile", profile);
      else if (step === 1) await api.post("/onboarding/business", biz);
      else if (step === 2) await api.post("/onboarding/clinic-page", page);
      await api.post("/onboarding/complete");
      await refreshUser();
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const startCheckout = async (plan) => {
    try {
      const { data } = await api.get(`/billing/checkout-url`, { params: { plan } });
      // Store intent so we can mark on return
      localStorage.setItem("toothpix_pending_plan", plan);
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const skipPayment = async () => {
    await refreshUser();
    navigate("/dashboard");
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-bone-100 flex flex-col">
      <header className="border-b border-bone-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles size={20} strokeWidth={1.5} className="text-forest-600" />
            <span className="font-heading text-2xl text-forest-700">Toothpix</span>
            <span className="overline ml-2 hidden md:inline">Setup</span>
          </div>
          <button
            onClick={() => {
              logout();
              navigate("/");
            }}
            className="text-xs text-forest-400 hover:text-forest-700"
            data-testid="onboarding-logout"
          >
            Save & exit
          </button>
        </div>
      </header>

      {/* Stepper */}
      <div className="max-w-3xl w-full mx-auto px-6 pt-10">
        <div className="flex items-center justify-between">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex-1 flex items-center">
              <div
                className={`w-8 h-8 flex items-center justify-center text-xs font-medium ${
                  i < step
                    ? "bg-forest-600 text-white"
                    : i === step
                    ? "border border-forest-600 text-forest-600"
                    : "border border-bone-200 text-forest-400"
                }`}
              >
                {i < step ? <Check size={14} /> : i + 1}
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-px mx-2 ${i < step ? "bg-forest-600" : "bg-bone-200"}`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2 text-[10px] uppercase tracking-[0.18em] text-forest-400">
          {STEPS.map((s) => (
            <div key={s.id} className="flex-1">{s.label}</div>
          ))}
        </div>
      </div>

      <main className="flex-1 max-w-2xl w-full mx-auto px-6 py-10 md:py-16">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="s0" {...fade}>
              <div className="overline mb-3">Step 1 of 4 · 90 seconds</div>
              <h1 className="font-heading text-4xl md:text-5xl text-forest-700 leading-tight">
                Hi! Let's get your clinic set up.
              </h1>
              <p className="text-forest-400 mt-3 max-w-md">
                Every field on this page is optional. Skip what you don't want
                to type. You can finish setup later from your dashboard.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
                <Field label="First name" value={profile.owner_first_name} onChange={(v) => setProfile({ ...profile, owner_first_name: v })} testid="ob-first-name" />
                <Field label="Last name" value={profile.owner_last_name} onChange={(v) => setProfile({ ...profile, owner_last_name: v })} testid="ob-last-name" />
                <Field label="Your title" value={profile.owner_title} onChange={(v) => setProfile({ ...profile, owner_title: v })} testid="ob-title" />
                <Field label="Direct phone" value={profile.owner_phone} onChange={(v) => setProfile({ ...profile, owner_phone: v })} testid="ob-phone" />
                <div className="md:col-span-2">
                  <Field label="Country" value={profile.country} onChange={(v) => setProfile({ ...profile, country: v })} testid="ob-country" />
                </div>
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div key="s1" {...fade}>
              <div className="overline mb-3">Step 2 of 4 · 30 seconds</div>
              <h1 className="font-heading text-4xl md:text-5xl text-forest-700 leading-tight">
                Tell us about your practice.
              </h1>
              <p className="text-forest-400 mt-3 max-w-md">
                Optional. Helps us suggest the right plan and pre-qualify
                leads for you.
              </p>
              <div className="space-y-5 mt-10">
                <Select
                  label="How many locations?"
                  value={biz.locations_count}
                  onChange={(v) => setBiz({ ...biz, locations_count: v })}
                  options={["", "1", "2", "3", "4-10", "10+"]}
                  testid="ob-locations"
                />
                <Select
                  label="Clear aligner cases per month"
                  value={biz.monthly_case_volume}
                  onChange={(v) => setBiz({ ...biz, monthly_case_volume: v })}
                  options={["", "<10", "10-25", "26-50", "51-100", "100+"]}
                  testid="ob-volume"
                />
                <Select
                  label="How did you hear about Toothpix?"
                  value={biz.referral_source}
                  onChange={(v) => setBiz({ ...biz, referral_source: v })}
                  options={["", "Instagram", "Google", "Word of mouth", "Industry conference", "Sales outreach", "Other"]}
                  testid="ob-referral"
                />
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="s2" {...fade}>
              <div className="overline mb-3">Step 3 of 4 · 90 seconds</div>
              <h1 className="font-heading text-4xl md:text-5xl text-forest-700 leading-tight">
                Your patient page.
              </h1>
              <p className="text-forest-400 mt-3 max-w-md">
                The only field that really matters here is the <b>booking URL</b>. That's where every smile preview sends patients next. Everything else has smart defaults.
              </p>
              <div className="space-y-5 mt-10">
                <Field
                  label="Booking URL (Calendly, your clinic site, anything)"
                  value={page.booking_url}
                  onChange={(v) => setPage({ ...page, booking_url: v })}
                  testid="ob-booking"
                  hint="Even your existing clinic website works. You can change this later."
                  placeholder="https://calendly.com/your-clinic"
                />
                <Field label="Tagline (shown above the photo upload)" value={page.clinic_tagline} onChange={(v) => setPage({ ...page, clinic_tagline: v })} testid="ob-tagline" />
                <Field label="Treatment name (used in your patient page & emails)" value={page.treatment_name} onChange={(v) => setPage({ ...page, treatment_name: v })} testid="ob-treatment" hint='e.g. "clear aligners", "Invisalign" if you are an authorized provider, or your own program name.' />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Clinic phone" value={page.phone} onChange={(v) => setPage({ ...page, phone: v })} testid="ob-clinic-phone" />
                  <Field label="Address" value={page.address} onChange={(v) => setPage({ ...page, address: v })} testid="ob-clinic-address" />
                </div>
                <details className="border border-bone-200 bg-white p-4">
                  <summary className="cursor-pointer text-sm text-forest-600 select-none">Optional: logo & brand color</summary>
                  <div className="mt-4 space-y-5">
                    <Field label="Logo URL" value={page.logo_url} onChange={(v) => setPage({ ...page, logo_url: v })} testid="ob-logo" />
                    <div>
                      <label className="label-flat">Brand color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={page.primary_color}
                          onChange={(e) => setPage({ ...page, primary_color: e.target.value })}
                          className="w-14 h-12 border border-bone-200 cursor-pointer"
                          data-testid="ob-color"
                        />
                        <input
                          className="input-flat font-mono text-sm"
                          value={page.primary_color}
                          onChange={(e) => setPage({ ...page, primary_color: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                </details>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="s3" {...fade}>
              <div className="overline mb-3">Step 4 of 4</div>
              <h1 className="font-heading text-4xl md:text-5xl text-forest-700 leading-tight">
                Pick a plan.
              </h1>
              <p className="text-forest-400 mt-3 max-w-lg">
                Your 14-day trial includes <b>20 free smile previews</b> so you can try the product on real patients. Upgrade any time to unlock the full plan, or keep exploring on trial.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
                <PlanCard
                  name="Clinic"
                  featured
                  price="$499"
                  per="/ month"
                  desc="3 locations · 250 previews / month · lead CRM · analytics · website embed"
                  cta="Choose Clinic"
                  onSelect={() => startCheckout("clinic")}
                  testid="plan-clinic"
                />
                <PlanCard
                  name="Group"
                  price="Custom"
                  per=""
                  desc="Unlimited locations · SSO · white-label portal"
                  cta="Talk to sales"
                  onSelect={() => { window.location.href = "mailto:hello@toothpix.ai"; }}
                  testid="plan-group"
                />
              </div>
              <button onClick={skipPayment} className="mt-8 text-sm text-forest-400 hover:text-forest-700 underline underline-offset-4" data-testid="skip-payment">
                I'll decide later, continue on free trial
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {step < 3 && (
          <div className="mt-12 space-y-4">
            <div className="flex items-center justify-between">
              <button
                onClick={back}
                disabled={step === 0}
                className="text-sm text-forest-400 hover:text-forest-700 disabled:opacity-30"
                data-testid="ob-back"
              >
                ← Back
              </button>
              <button onClick={next} disabled={saving} className="btn-primary" data-testid="ob-next">
                {saving ? "Saving…" : (<>Continue <ArrowRight size={14} /></>)}
              </button>
            </div>
            <div className="text-center pt-2 border-t border-bone-200">
              <button
                onClick={skipAll}
                disabled={saving}
                className="text-xs text-forest-400 hover:text-forest-700 underline underline-offset-4"
                data-testid="ob-skip-all"
              >
                Skip. I'll finish setup later from my dashboard
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const fade = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.4, ease: "easeOut" },
};

function Field({ label, value, onChange, type = "text", required, hint, testid, placeholder }) {
  return (
    <div>
      <label className="label-flat">{label}</label>
      <input
        type={type}
        className="input-flat"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        placeholder={placeholder}
      />
      {hint && <div className="text-xs text-forest-400 mt-1">{hint}</div>}
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="label-flat">{label}</label>
      <select
        className="input-flat bg-white"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={o} value={o}>{o || "Select…"}</option>
        ))}
      </select>
    </div>
  );
}

function PlanCard({ name, price, per, desc, cta, featured, onSelect, testid }) {
  return (
    <div className={`p-6 border ${featured ? "bg-forest-600 text-white border-forest-600" : "bg-white border-bone-200"}`}>
      <div className={`overline ${featured ? "!text-bone-200/70" : ""}`}>{name}</div>
      <div className="font-heading text-4xl mt-2 flex items-baseline gap-1">
        {price}
        <span className={`text-sm font-body ${featured ? "text-bone-100/70" : "text-forest-400"}`}>{per}</span>
      </div>
      <p className={`mt-3 text-sm leading-relaxed ${featured ? "text-bone-100/80" : "text-forest-400"}`}>{desc}</p>
      <button
        onClick={onSelect}
        data-testid={testid}
        className={`mt-6 inline-flex items-center gap-2 px-5 py-2.5 border text-sm transition-colors ${
          featured
            ? "bg-white text-forest-600 border-white hover:bg-bone-100"
            : "border-forest-600 text-forest-600 hover:bg-forest-600 hover:text-white"
        }`}
      >
        {cta} <ArrowRight size={14} />
      </button>
    </div>
  );
}
